import { format, parse, parseISO } from "date-fns"
import DatePicker from "legacy/DatePicker"
import { handleInputChange } from "legacy/helpers.es6.jsx"
import { validatorFactory } from "legacy/validatorFactory"
import PropTypes from "prop-types"
import React from "react"

import Modal from "src/components/Modal"

class EditNoteModal extends React.Component {
  constructor(props) {
    super(props)

    // Parse dates with better error handling
    const parseDate = (dateString) => {
      if (!dateString) return new Date() // fallback to current date if null/undefined

      // Try parseISO first (for ISO format like "2025-01-15")
      try {
        const date = parseISO(dateString)
        if (!isNaN(date)) return date
      } catch (e) {
        // parseISO failed, continue to fallback
      }

      // Try Date constructor as fallback
      const date = new Date(dateString)
      if (!isNaN(date)) return date

      // If all else fails, return current date
      console.warn(`Failed to parse date: ${dateString}`)
      return new Date()
    }

    this.state = {
      startDate: parseDate(this.props.startDate),
      endDate: parseDate(this.props.endDate),
      text: this.props.text,
      showModal: false,
      saving: false,
    }

    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleDateChange = this.handleDateChange.bind(this)
    this.formatDateForDisplay = this.formatDateForDisplay.bind(this)
    this.save = this.save.bind(this)
    this.openModal = this.openModal.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.deleteNote = this.deleteNote.bind(this)
    this.validator = validatorFactory()
  }

  handleInputChange(event) {
    handleInputChange(this, event)
  }

  handleDateChange(field) {
    return (dateString) => {
      // DatePicker returns a string in MM/dd/yyyy format
      if (dateString) {
        try {
          const date = parse(dateString, "MM/dd/yyyy", new Date())
          if (!isNaN(date)) {
            this.setState({ [field]: date })
          }
        } catch (e) {
          console.warn(`Failed to parse date for ${field}:`, dateString)
        }
      } else {
        this.setState({ [field]: null })
      }
    }
  }

  formatDateForDisplay(date) {
    return date && !isNaN(date) ? format(date, "MM/dd/yyyy") : ""
  }

  save() {
    if (this.validator.allValid()) {
      this.setState({ saving: true })
      const params = {
        text: this.state.text,
        start_date: this.formatDateForDisplay(this.state.startDate),
        end_date: this.formatDateForDisplay(this.state.endDate),
        _method: "PATCH",
      }
      this.ajaxSave(params)
    } else {
      this.validator.showMessages()
      this.forceUpdate()
    }
  }

  ajaxSave(params) {
    $.post(this.props.noteRoute, params, () => {
      location.reload()
    }).fail((data) => {
      this.setState({
        errorMessage: "Error creating note.",
        saving: false,
      })
    })
  }

  openModal() {
    this.setState({ showModal: true })
  }

  closeModal() {
    this.setState({ showModal: false })
  }

  deleteNote() {
    $.post(this.props.noteRoute, { _method: "DELETE" }, () => {
      location.reload()
    }).fail((data) => {
      this.setState({
        errorMessage: "Error deleting note.",
        saving: false,
      })
    })
  }

  deleteButton() {
    return (
      <button
        className="delete-button btn btn-danger float-left"
        onClick={this.deleteNote}
        type="button"
      >
        Delete
      </button>
    )
  }

  render() {
    return (
      <div className="inline">
        <a
          className="edit-note-button float-right"
          onClick={this.openModal}
          href="#"
        >
          Edit
        </a>
        <Modal
          isOpen={this.state.showModal}
          onClose={this.closeModal}
          size="mediumFixed"
        >
          <Modal.Header title="Edit Note" />
          <Modal.Body>
            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label>Start Date</label>
                  <div className="form-control-with-icon">
                    <i className="icon icon-calendar form-control-icon-left" />
                    <DatePicker
                      setParentState={this.handleDateChange("startDate")}
                      name="startDate"
                      value={this.formatDateForDisplay(this.state.startDate)}
                      placeholder="mm/dd/yyyy"
                      orientation="top"
                    />
                  </div>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label>End Date</label>
                  <div className="form-control-with-icon">
                    <i className="icon icon-calendar form-control-icon-left" />
                    <DatePicker
                      setParentState={this.handleDateChange("endDate")}
                      name="endDate"
                      value={this.formatDateForDisplay(this.state.endDate)}
                      placeholder="mm/dd/yyyy"
                      orientation="top"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm">
                <div className="form-group">
                  <label>Note</label>
                  <textarea
                    className="note-text form-control"
                    onChange={this.handleInputChange}
                    name="text"
                    value={this.state.text}
                    required="required"
                    placeholder="Enter any information that you want to see about this time period"
                  />
                  {this.validator.message(
                    "note_text",
                    this.state.text,
                    "required"
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex justify-between space-x-2">
              <div>{this.deleteButton()}</div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={this.closeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={this.save}
                  disabled={this.state.saving}
                >
                  {this.state.saving ? (
                    <>
                      Saving...
                      <i
                        className="icon icon-spin icon-spinner ml-2"
                        role="img"
                        aria-label="loading indicator"
                      />
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    )
  }
}

EditNoteModal.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  noteRoute: PropTypes.string.isRequired,
}

export default EditNoteModal
